import { motion, AnimatePresence } from 'motion/react'
import {
  UtensilsCrossed, Plane, Receipt, Home, Heart, PawPrint,
  Building2, Database, Plus, FileText, BarChart3,
  Puzzle, Scale, MessageSquare, Mail, Calendar, Search,
  Trash2, RefreshCw,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CatalogPlugin, PluginSettingDef, PluginConnectionDetails } from '../types'
import { ToggleSwitch, AnimatedChevron } from '@tendbot/ui'
import { expandCollapse } from '@tendbot/ui'
import PluginSettings from './PluginSettings'
import PluginConnectionExpanded from './PluginConnectionExpanded'

const ICONS: Record<string, LucideIcon> = {
  UtensilsCrossed, Plane, Receipt, Home, Heart, PawPrint,
  Building2, Database, Puzzle, FileText, BarChart3, Scale,
  MessageSquare, Mail, Calendar, Search,
}

interface PluginCardProps {
  plugin: CatalogPlugin
  enabled: boolean
  settingDefs?: PluginSettingDef[]
  userSettings?: Record<string, unknown>
  onToggle?: () => void
  onInstall?: () => void
  onUninstall?: () => void
  expanded?: boolean
  onExpand?: () => void
  installable?: boolean
  // Connection props
  connectionDetails?: PluginConnectionDetails | null
  allConnections?: PluginConnectionDetails[]
  defaultConnectionId?: string | null
  connecting?: boolean
  onConnect?: () => void
  onConnectGmail?: () => void
  onConnectCalendar?: () => void
  onConnectContacts?: () => void
  onDisconnect?: (connectionId?: string) => void
  onSetDefault?: (connectionId: string) => void
  onAddAccount?: () => void
  onToggleCalendarSync?: (calendarId: string, syncEnabled: boolean) => void
  onToggleServiceSync?: (connectionId: string, services: { calendarSyncEnabled?: boolean; emailSyncEnabled?: boolean }) => void
  onRunThrough?: () => Promise<void>
  runThroughLastRunAt?: string | null
}

export default function PluginCard({
  plugin,
  enabled,
  settingDefs,
  userSettings,
  onToggle,
  onInstall,
  onUninstall,
  expanded,
  onExpand,
  installable,
  connectionDetails,
  allConnections,
  defaultConnectionId,
  connecting,
  onConnect,
  onConnectGmail,
  onConnectCalendar,
  onConnectContacts,
  onDisconnect,
  onSetDefault,
  onAddAccount,
  onToggleCalendarSync,
  onToggleServiceSync,
  onRunThrough,
  runThroughLastRunAt,
}: PluginCardProps): React.JSX.Element {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('plugins')
  const Icon: LucideIcon = ICONS[plugin.icon] || Puzzle

  const isInstalled = plugin.installed
  const isConnected = !!connectionDetails

  // For auth-required plugins: show Connect instead of Add when not connected
  const showConnectButton = plugin.authRequired && !isConnected && onConnect
  // Show toggle only when connected (or when no auth required)
  const showToggle = isInstalled && onToggle && (!plugin.authRequired || isConnected)

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ touchAction: 'pan-y' }}
      className={`glass-card rounded-xl overflow-hidden border transition-all duration-200 ${
        enabled
          ? 'border-gold/20 bg-gold/[0.03]'
          : 'border-white/10'
      }`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${plugin.color}15`, borderColor: `${plugin.color}25`, borderWidth: 1 }}
          >
            <Icon size={18} style={{ color: plugin.color }} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-ivory text-sm font-medium truncate">{plugin.name}</h4>
              {plugin.verified && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald/10 text-emerald border border-emerald/20">
                  {t('card.verified')}
                </span>
              )}
              {plugin.id === 'google-workspace' && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/30">
                  {t('card.beta')}
                </span>
              )}
              {isConnected && (
                <div className="w-2 h-2 rounded-full bg-emerald shrink-0" />
              )}
            </div>
            <p className="text-pearl/60 text-xs mt-0.5 line-clamp-2">{plugin.description}</p>

            {/* Meta row */}
            <div className="flex items-center gap-3 mt-2">
              {plugin.author && (
                <span className="text-xs text-pearl/50">
                  {t('card.byAuthor', { author: plugin.author })}
                </span>
              )}
              {plugin.version && (
                <span className="text-xs text-pearl/50">
                  v{plugin.version}
                </span>
              )}
            </div>
          </div>

          {/* Action */}
          <div className="shrink-0">
            {showToggle && (
              <ToggleSwitch checked={enabled} onChange={onToggle} />
            )}
            {showConnectButton && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onConnect}
                disabled={connecting}
                className="py-1.5 px-3 rounded-full bg-gold/10 text-gold text-xs font-medium hover:bg-gold/20 transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                {connecting ? (
                  <><RefreshCw size={12} className="animate-spin" /> {tc('actions.connecting')}</>
                ) : (
                  <>{tc('actions.connect')}</>
                )}
              </motion.button>
            )}
            {installable && !plugin.authRequired && onInstall && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onInstall}
                className="py-1.5 px-3 rounded-full bg-gold/10 text-gold text-xs font-medium hover:bg-gold/20 transition-colors flex items-center gap-1"
              >
                <Plus size={12} />
                {t('card.add')}
              </motion.button>
            )}
            {installable && plugin.authRequired && isConnected && onInstall && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onInstall}
                className="py-1.5 px-3 rounded-full bg-gold/10 text-gold text-xs font-medium hover:bg-gold/20 transition-colors flex items-center gap-1"
              >
                <Plus size={12} />
                {tc('actions.enable')}
              </motion.button>
            )}
          </div>
        </div>

        {/* Expanded settings (for installed plugins) */}
        {isInstalled && onExpand && (
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="flex items-center justify-between">
              <button
                onClick={onExpand}
                className="flex items-center gap-1 text-xs text-gold/60 hover:text-gold transition-colors"
              >
                <AnimatedChevron isOpen={!!expanded} size={12} className="text-gold/60" />
                {expanded ? t('card.hideDetails') : (isConnected ? t('card.connectionDetails') : t('card.pluginSettings'))}
              </button>
              {onUninstall && !plugin.authRequired && (
                <button
                  onClick={onUninstall}
                  className="flex items-center gap-1 text-xs text-rose/50 hover:text-rose transition-colors"
                >
                  <Trash2 size={10} />
                  {t('card.remove')}
                </button>
              )}
            </div>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  {...expandCollapse}
                  className="overflow-hidden"
                >
                  {/* Connection details for auth-required plugins */}
                  {isConnected && connectionDetails && onDisconnect && (
                    <div className="pt-3">
                      <PluginConnectionExpanded
                        connectionDetails={connectionDetails}
                        allConnections={allConnections}
                        defaultConnectionId={defaultConnectionId}
                        onConnectGmail={onConnectGmail}
                        onConnectCalendar={onConnectCalendar}
                        onConnectContacts={onConnectContacts}
                        onDisconnect={onDisconnect}
                        onSetDefault={onSetDefault}
                        onAddAccount={onAddAccount}
                        onToggleCalendarSync={onToggleCalendarSync}
                        onToggleServiceSync={onToggleServiceSync}
                        onRunThrough={onRunThrough}
                        runThroughLastRunAt={runThroughLastRunAt}
                      />
                    </div>
                  )}

                  {/* Regular plugin settings */}
                  {!isConnected && (
                    <PluginSettings
                      pluginId={plugin.id}
                      settingDefs={settingDefs}
                      userSettings={userSettings}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  )
}
